import React, { useEffect, useState } from "react";
import { useCodeGeneration } from "../context/CodeGenerationContext";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { useTranslation } from "react-i18next";
import { Loader2, X, CheckCircle2, XCircle } from "lucide-react";

const FEEDBACK_LABELS = {
    file_reading: "thinkingLookingAtFiles",
    grep_search: "thinkingSearchingInFiles",
    glob_search: "thinkingFindingFiles",
    file_search: "thinkingFindingFiles",
    line_replace: "thinkingMakingChanges",
    edit_file: "thinkingEditingFile",
    code_generation: "thinkingWritingCode",
    component_creation: "thinkingBuildingComponent",
    file_deletion: "thinkingRemovingFile",
    read_application_logs: "thinkingCheckingLogs",
    code_execution: "thinkingRunningCode",
    planning: "thinkingPlanning",
    generation_start: "thinkingStarting",
    todo_write: "thinkingUpdatingTasks",
    explore_codebase: "thinkingInvokingSubagent",
    search_web: "thinkingSearchingWeb",
    browser_agent: "thinkingBrowserAgentRunning",
    browser_agent_session: "thinkingBrowserAgentRunning",
    browser_agent_step: "thinkingBrowserAgentRunning",
    data_inspection: "thinkingInspectingData",
    code_read: "thinkingReadingStepCode",
    code_edit: "thinkingEditingStepCode",
    code_search: "thinkingSearchingStepCode",
    step_creation: "thinkingCreatingStep",
    testing_agent: "thinkingRunningTests",
};

export function CodeGenerationModal() {
    const {
        showModal,
        isGeneratingCode,
        taskStatus,
        generationStartTime,
        currentWorkflowId,
        latestFeedback,
        hideGenerationModal,
    } = useCodeGeneration();

    const auth = useAuth();
    const interfaceStyle = auth?.interfaceStyle;
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [elapsedTime, setElapsedTime] = useState(0);

    useEffect(() => {
        if (isGeneratingCode && generationStartTime) {
            const interval = setInterval(() => {
                setElapsedTime(
                    Math.floor((Date.now() - generationStartTime) / 1000)
                );
            }, 1000);
            return () => clearInterval(interval);
        }
    }, [isGeneratingCode, generationStartTime]);

    const handleBackToWorkflow = () => {
        if (!currentWorkflowId) return;
        navigate(
            interfaceStyle === "expert"
                ? `/workflows/${currentWorkflowId}`
                : `/chat/${currentWorkflowId}/chat`
        );
        hideGenerationModal();
    };

    if (!showModal) return null;

    const isCompleted = taskStatus === "COMPLETED";
    const isFailed = taskStatus === "FAILED" || taskStatus === "CANCELLED";
    const isRunning = isGeneratingCode && !isCompleted && !isFailed;
    const mins = Math.floor(elapsedTime / 60);
    const secs = (elapsedTime % 60).toString().padStart(2, "0");

    const feedbackKey = latestFeedback && FEEDBACK_LABELS[latestFeedback];
    const feedbackLabel = feedbackKey
        ? t(`chatMessages.${feedbackKey}`)
        : null;

    return (
        <div className="fixed bottom-4 left-4 z-50 flex items-center gap-2 px-3 py-2 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 rounded-lg shadow-md border border-gray-200 dark:border-gray-600 text-xs">
            {isRunning && (
                <>
                    <Loader2 className="h-3.5 w-3.5 animate-spin text-blue-500 shrink-0" />
                    <span className="text-gray-600 dark:text-gray-300 max-w-48 truncate">
                        {feedbackLabel ||
                            t(
                                "codeGenerationModal.generating",
                                "Generating..."
                            )}
                    </span>
                    {elapsedTime > 0 && (
                        <span className="text-gray-400 font-mono">
                            {mins}:{secs}
                        </span>
                    )}
                </>
            )}
            {isCompleted && (
                <>
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-500 shrink-0" />
                    <span className="text-green-600 dark:text-green-400">
                        {t("codeGenerationModal.codeGenerationCompleted")}
                    </span>
                </>
            )}
            {isFailed && (
                <>
                    <XCircle className="h-3.5 w-3.5 text-red-500 shrink-0" />
                    <span className="text-red-600 dark:text-red-400">
                        {taskStatus === "CANCELLED"
                            ? t("codeGenerationModal.cancelled", "Cancelled")
                            : t("codeGenerationModal.failed", "Failed")}
                    </span>
                </>
            )}
            {currentWorkflowId && (
                <button
                    onClick={handleBackToWorkflow}
                    className="ml-1 text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline"
                >
                    {t("codeGenerationModal.backToWorkflow")}
                </button>
            )}
            <button
                onClick={hideGenerationModal}
                className="ml-1 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200 rounded"
                aria-label={t("common.close", "Close")}
            >
                <X className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}
